import Link from 'next/link';
import {
  type FC,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { LootboxBonusType, type LootboxItem } from '@/api/common';
import { IconComponent } from '@/components/IconComponent';
import { PromotionPageTabs } from '@/modules/promotions/components/PromotionsPage';
import { LootboxState, lootboxAnimationSrc } from '../../helpers';
import { LootboxButton } from '../LootboxButton';
import { ScLootboxItem } from './styled';

interface LootboxProps {
  lootbox: LootboxItem;
  isSelected: boolean;
  isChoiceMade: boolean;
  index: number;
  handleSelect: (id: number) => void;
  onClose: () => void;
}

export const Lootbox: FC<LootboxProps> = ({
  lootbox: { bonuses },
  isSelected,
  isChoiceMade,
  handleSelect,
  index,
  onClose,
}) => {
  const { t } = useTranslation('lootboxes');

  const [lootboxState, setLootboxState] = useState(LootboxState.CLOSED);

  const lootboxImageSrc = useMemo(
    () => lootboxAnimationSrc(lootboxState),
    [lootboxState]
  );

  const isLootboxEmpty = useMemo(
    () => bonuses.length && bonuses[0].type === LootboxBonusType.EmptyBonus,
    [bonuses]
  );

  const normalizedBonuses = useMemo(
    () =>
      bonuses.map(({ title }, i) => (
        <Fragment key={i}>
          {title}{' '}
          {bonuses.length > i + 1 && (
            <IconComponent name="plusIcon" size="24px" />
          )}
        </Fragment>
      )),
    [bonuses]
  );

  const handleClick = useCallback(() => {
    handleSelect(index);
  }, [handleSelect, index]);

  useEffect(() => {
    isSelected &&
      setLootboxState(
        isLootboxEmpty
          ? LootboxState.IS_OPENING_EMPTY
          : LootboxState.IS_OPENING_FULL
      );
  }, [isLootboxEmpty, isSelected]);

  useEffect(() => {
    if (lootboxState === LootboxState.IS_OPENING_FULL) {
      const timeoutId = setTimeout(
        () => setLootboxState(LootboxState.OPENED_FULL),
        5000
      );

      return () => clearTimeout(timeoutId);
    }
  }, [lootboxState]);

  return (
    <ScLootboxItem isChoiceMade={isChoiceMade} isSelected={isSelected}>
      <div className="mobile-click-wrapper" onClick={handleClick}>
        <object
          aria-label={lootboxState}
          data={lootboxImageSrc}
          type="image/svg+xml"
        />
      </div>
      {isChoiceMade ? (
        <>
          <div className="bonus">
            {isLootboxEmpty ? t('empty') : normalizedBonuses}
          </div>
          {isSelected ? (
            isLootboxEmpty ? (
              <div className="empty-text">{t('emptyPrize')}</div>
            ) : (
              <Link
                href={`/promotions?tab=${PromotionPageTabs.ACTIVE_BONUSES}`}
                onClick={onClose}
              >
                <LootboxButton>{t('activate')}</LootboxButton>
              </Link>
            )
          ) : null}
        </>
      ) : (
        <div className="button-container">
          <LootboxButton handleClick={handleClick}>{t('open')}</LootboxButton>
        </div>
      )}
    </ScLootboxItem>
  );
};
